import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTours } from '../tours/TourProvider'
import {
  discoveryBeta,
  DiscoveryApiError,
  type DiscoveryAnswer,
  type DiscoveryFormMeta,
  type DiscoveryTemplateMeta,
} from '../api/discovery'

function parseEmails(raw: string): string[] {
  return raw
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean)
}

function guestUrl(path: string): string {
  return `${window.location.origin}${window.location.pathname}${path}`
}

/**
 * Beta Discovery — operator surface. Create a form (template or goal-authored,
 * same helpers Ellis' start_discovery tool uses), activate it, relay the guest
 * link to the thread, watch answers land. Owner-scoped; thread parties only
 * on the guest side.
 */
export function DiscoveryPage() {
  const [params] = useSearchParams()
  const preselect = params.get('example')
  const { playTour } = useTours()

  const [templates, setTemplates] = useState<DiscoveryTemplateMeta[]>([])
  const [forms, setForms] = useState<DiscoveryFormMeta[]>([])
  const [answers, setAnswers] = useState<Record<string, DiscoveryAnswer[]>>({})
  const [links, setLinks] = useState<Record<string, string>>({})
  const [sentTo, setSentTo] = useState<Record<string, string[]>>({})
  const [expanded, setExpanded] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [templateId, setTemplateId] = useState(preselect ?? 'quick')
  const [audience, setAudience] = useState('')
  const [goal, setGoal] = useState('')

  const refresh = useCallback(async () => {
    const res = await discoveryBeta.listForms()
    setForms(res.forms)
  }, [])

  useEffect(() => {
    void discoveryBeta
      .templates()
      .then((r) => setTemplates(r.templates))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'templates_failed'),
      )
    void refresh().catch((err: unknown) =>
      setError(err instanceof Error ? err.message : 'list_failed'),
    )
  }, [refresh])

  const selected = useMemo(
    () => templates.find((t) => t.id === templateId) ?? null,
    [templates, templateId],
  )

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed')
    } finally {
      setBusy(false)
    }
  }

  const create = () =>
    void run(async () => {
      const emails = parseEmails(audience)
      if (!emails.length) {
        setError('Add at least one thread-party email for the audience.')
        return
      }
      await discoveryBeta.createForm({
        audience_emails: emails,
        example: templateId,
        ...(goal.trim() ? { goal: goal.trim() } : {}),
      })
      setAudience('')
      setGoal('')
      await refresh()
    })

  const activate = (formId: string) =>
    void run(async () => {
      const res = await discoveryBeta.activate(formId)
      setLinks((prev) => ({ ...prev, [formId]: res.guest_path }))
      await refresh()
    })

  const emailLink = (formId: string) =>
    void run(async () => {
      try {
        const res = await discoveryBeta.sendLink(formId)
        setLinks((prev) => ({ ...prev, [formId]: res.guest_path }))
        setSentTo((prev) => ({ ...prev, [formId]: res.sent_to }))
      } catch (err) {
        if (err instanceof DiscoveryApiError && err.status === 503) {
          setError(
            'Email delivery is not configured on this environment — use Activate and relay the link yourself.',
          )
          return
        }
        throw err
      }
      await refresh()
    })

  const revoke = (formId: string) =>
    void run(async () => {
      await discoveryBeta.revoke(formId)
      await refresh()
    })

  const toggle = (formId: string) =>
    void run(async () => {
      if (expanded === formId) {
        setExpanded(null)
        return
      }
      const detail = await discoveryBeta.getForm(formId)
      setAnswers((prev) => ({ ...prev, [formId]: detail.answers }))
      setExpanded(formId)
    })

  return (
    <div className="shell">
      <header className="top compact">
        <div className="brand">
          <span className="mark">BeyondQuota</span>
          <h1>Discovery</h1>
        </div>
        <p className="lede tight">
          Schema-driven forms for a thread — only the parties you list can
          unlock and answer. Answers land here as JSON.
        </p>
        <button
          type="button"
          className="ghost tour-replay-pill"
          onClick={() => playTour('discovery')}
        >
          ? Take the tour
        </button>
      </header>

      {error ? (
        <p className="banner" role="alert">
          {error}
        </p>
      ) : null}

      <form
        className="k-inline-form preview-form stack"
        onSubmit={(e) => {
          e.preventDefault()
          create()
        }}
      >
        <div className="segs" role="group" aria-label="Template" data-tour="discovery.templates">
          {(templates.length ? templates : [{ id: 'quick', title: 'quick', lede: '', field_count: 0 }]).map(
            (t) => (
              <button
                key={t.id}
                type="button"
                className={templateId === t.id ? 'seg on' : 'seg'}
                aria-pressed={templateId === t.id}
                onClick={() => setTemplateId(t.id)}
                disabled={!templates.length}
              >
                {t.id}
              </button>
            ),
          )}
        </div>
        {selected ? <span className="ctl-hint">{selected.lede}</span> : null}
        <label className="ctl grow preview-full" data-tour="discovery.audience">
          <span>Audience (thread parties, comma-separated)</span>
          <input
            type="text"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            placeholder="champion@acme.com, ae@acme.com"
            spellCheck={false}
            required
          />
        </label>
        <label className="ctl grow preview-full" data-tour="discovery.goal">
          <span>Goal (optional — shapes the form like Ellis would)</span>
          <input
            type="text"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="Qualify the Genie rollout before a working session"
            spellCheck={false}
          />
        </label>
        <button type="submit" className="ghost" disabled={busy} data-tour="discovery.create">
          {busy ? '…' : 'Create draft'}
        </button>
      </form>

      <ul className="todo-list" aria-label="Discovery forms">
        {forms.map((f, idx) => {
          const link = links[f.id]
          const isOpen = expanded === f.id
          const formAnswers = answers[f.id] ?? []
          return (
            <li
              key={f.id}
              className={`todo status-${f.status}`}
              {...(idx === 0 ? { 'data-tour': 'discovery.form-card' } : {})}
            >
              <div className="todo-main">
                <button
                  type="button"
                  className="todo-body"
                  onClick={() => toggle(f.id)}
                >
                  <span className="todo-title">{f.title}</span>
                  <span className="todo-preview">
                    {f.owner_email ? `for ${f.owner_email} · ` : ''}
                    {f.audience_emails.join(' · ')}
                    {f.answer_count
                      ? ` · ${f.answer_count} answer${f.answer_count === 1 ? '' : 's'}`
                      : ''}
                  </span>
                </button>
                <div className="todo-meta">
                  <span className={`pill status-pill status-${f.status}`}>
                    {f.status}
                  </span>
                  {f.status === 'draft' || f.status === 'active' ? (
                    <button
                      type="button"
                      className="ghost"
                      disabled={busy}
                      onClick={() => emailLink(f.id)}
                      title="Activate + email the guest link to every audience member via Ellis"
                    >
                      Email link
                    </button>
                  ) : null}
                  {f.status === 'draft' ? (
                    <button
                      type="button"
                      className="ghost"
                      disabled={busy}
                      onClick={() => activate(f.id)}
                    >
                      Activate
                    </button>
                  ) : null}
                  {f.status === 'active' ? (
                    <button
                      type="button"
                      className="ghost"
                      disabled={busy}
                      onClick={() => revoke(f.id)}
                    >
                      Revoke
                    </button>
                  ) : null}
                </div>
              </div>

              {sentTo[f.id] ? (
                <div className="controls todoist preview-actions">
                  <span className="ctl-hint">
                    Link emailed to {sentTo[f.id].join(', ')} ✓
                  </span>
                </div>
              ) : null}

              {link ? (
                <div className="controls todoist preview-actions">
                  <span className="ctl-hint">
                    {sentTo[f.id]
                      ? 'Guest link (already emailed):'
                      : 'Guest link (relay to the thread):'}
                  </span>
                  <a className="ghost" href={guestUrl(link)}>
                    {guestUrl(link)}
                  </a>
                </div>
              ) : null}

              {isOpen ? (
                <details className="detail-accordion" open>
                  <summary>Answers</summary>
                  {formAnswers.length ? (
                    <ul className="todo-list">
                      {formAnswers.map((a) => (
                        <li key={a.respondent_email} className="todo">
                          <div className="todo-main">
                            <div className="todo-body">
                              <span className="todo-title">
                                {a.respondent_email}
                              </span>
                              <pre className="preview-json">
                                {JSON.stringify(a.answers, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="empty">No answers yet.</p>
                  )}
                </details>
              ) : null}
            </li>
          )
        })}
      </ul>

      {!forms.length ? (
        <p className="empty">
          No forms yet — create one above, or let Ellis draft one from a thread
          with <code>start_discovery</code>.
        </p>
      ) : null}

      {import.meta.env.DEV ? (
        <p className="lede tight">
          Looking for the local demos? <Link to="/previews">Previews</Link>
        </p>
      ) : null}
    </div>
  )
}
